'use client'
import React from 'react'

const CanvasAsciiBackground: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)
  const animationFrameRef = React.useRef<number | null>(null)
  const charsRef = React.useRef<
    Array<
      Array<{
        char: string
        frame: number
        totalFrames: number
        isAnimating: boolean
      }>
    >
  >([])
  const activeAnimationsRef = React.useRef<Set<string>>(new Set())

  const FONT_SIZE = 16
  const ASCII_CHARS = ".-':_,^=;><+!rc*/z?v)J7(|i{C}fI31tlu[3489afdhg)(#$ne]-VERCEL-AKXH/NVIDIA/RD#$WQ%&@"

  const generateRandomChar = React.useCallback((): string => {
    return ASCII_CHARS[Math.floor(Math.random() * ASCII_CHARS.length)]
  }, [])

  const draw = React.useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const { width, height } = canvas
    const cols = Math.ceil(width / (FONT_SIZE * 0.6))
    const rows = Math.ceil(height / FONT_SIZE)

    // Initialize if needed
    if (charsRef.current.length === 0) {
      charsRef.current = Array(rows)
        .fill(undefined)
        .map(() =>
          Array(cols)
            .fill(undefined)
            .map(() => ({
              char: ' ',
              frame: 0,
              totalFrames: 0,
              isAnimating: false,
            }))
        )
    }

    // Clear canvas
    ctx.clearRect(0, 0, width, height)
    ctx.font = `${FONT_SIZE}px monospace`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#444'

    // Update and draw characters
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const char = charsRef.current[y][x]
        const key = `${x},${y}`

        if (char.isAnimating) {
          char.frame++
          if (char.frame >= char.totalFrames) {
            char.isAnimating = false
            char.char = '·'
            activeAnimationsRef.current.delete(key)
          } else {
            char.char = generateRandomChar()
          }
        }

        ctx.fillText(char.char, x * (FONT_SIZE * 0.6) + FONT_SIZE * 0.3, y * FONT_SIZE + FONT_SIZE / 2)
      }
    }

    animationFrameRef.current = requestAnimationFrame(draw)
  }, [generateRandomChar])

  const handleMouseMove = React.useCallback(
    (event: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current
      if (!canvas) return

      const rect = canvas.getBoundingClientRect()
      const x = Math.floor((event.clientX - rect.left) / (FONT_SIZE * 0.6))
      const y = Math.floor((event.clientY - rect.top) / FONT_SIZE)
      const range = 8

      if (activeAnimationsRef.current.size >= 150) return

      for (let i = 0; i < 6; i++) {
        const dx = Math.floor(Math.random() * (range * 2 + 1)) - range
        const dy = Math.floor(Math.random() * (range * 2 + 1)) - range
        const newX = x + dx
        const newY = y + dy

        if (newX >= 0 && newY >= 0 && newY < charsRef.current.length && newX < (charsRef.current[0]?.length || 0)) {
          const key = `${newX},${newY}`
          if (!activeAnimationsRef.current.has(key)) {
            activeAnimationsRef.current.add(key)
            charsRef.current[newY][newX] = {
              char: generateRandomChar(),
              frame: 0,
              totalFrames: Math.floor(Math.random() * 20) + 15,
              isAnimating: true,
            }
          }
        }
      }
    },
    [generateRandomChar]
  )

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
      charsRef.current = [] // Reset to trigger reinitialization
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    animationFrameRef.current = requestAnimationFrame(draw)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [draw])

  return <canvas ref={canvasRef} className="fixed inset-0 z-0" onMouseMove={handleMouseMove} />
}

export default CanvasAsciiBackground
